/**
 * Quick Review board table wired to paginated list API fetches.
 * @module quickReviewOrdersTable
 */
import { useDispatch } from "react-redux";
import CustomTable from "../../../components/custom-table/custom-table";
import { ModalId } from "../../../enums/modalId";
import { TableNames } from "../../../enums/tableNames";
import { fetchQuickReviewCount } from "../../../redux/slice/medicalPa/quickReviewCountSlice";
import { setOpenedModalId } from "../../../redux/slice/modalSlice";
import { AppDispatch } from "../../../redux/store/store";
import { useInitialDataFetchingForTable } from "./hooks/useInitialDataFetchingForTable";
import { useNextPageDataFetch } from "./hooks/useNextPageDataFetch";
import { useQuickReviewOrdersTableData } from "./hooks/useQuickReviewOrdersTableData";
import {
  QuickReviewTableData,
  QuickReviewTableUtils,
} from "./utils/quickReviewTableUtils";

/**
 * Renders the Quick Review board with infinite-scroll pagination.
 *
 * @returns CustomTable configured for Quick Review columns and fetches
 */
const QuickReviewOrdersTable = () => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    quickReviewOrdersState,
    setQuickReviewOrdersState,
    tableData,
    setTableData,
  } = useQuickReviewOrdersTableData();
  const { initialDataFetchingExecuted, setInitialDataFetchingExecuted } =
    useInitialDataFetchingForTable(setQuickReviewOrdersState, setTableData);

  const { setEndIndex } = useNextPageDataFetch(
    initialDataFetchingExecuted,
    quickReviewOrdersState,
    setQuickReviewOrdersState,
    setTableData,
  );

  /**
   * Opens the Quick Review detail modal for the clicked board row.
   *
   * @param row - Table row whose `id` is the PA order id
   */
  const handleRowClick = (row: QuickReviewTableData) => {
    dispatch(
      setOpenedModalId({
        id: ModalId.QUICK_REVIEW_MODAL,
        metaData: {
          orderId: row?.id ?? "",
          onDecisionSuccess: () => {
            setInitialDataFetchingExecuted(false);
            dispatch(fetchQuickReviewCount());
          },
        },
      }),
    );
  };

  return (
    <div className="h-full">
      <CustomTable
        tableHeaders={QuickReviewTableUtils.generateColumnsForQuickReviewTable()}
        tableData={tableData}
        tableName={TableNames.QUICK_REVIEW_TABLE}
        itemsPerPage={10}
        pagesPerView={5}
        count={tableData?.length ?? 0}
        totalCount={quickReviewOrdersState?.data?.total_count ?? 0}
        endIndexOfTable={setEndIndex}
        hideSearchBar
        showInLineLoader={quickReviewOrdersState.showinLineLoader}
        isFetching={quickReviewOrdersState.loading}
        onRowClick={handleRowClick}
      />
    </div>
  );
};

export default QuickReviewOrdersTable;
